import axios, { AxiosInstance, AxiosResponse } from 'axios';
import { config as appConfig } from '../config/environment';
import { readJwtClaims } from '../library/jwtClaims';
import { attachSession, sessionStore } from './sessionKeeper';
import {
    AuthResponse,
    User,
    UserLoginRequest,
    UserRegistrationRequest,
    UserUpdateRequest
} from '../types';

class ApiService {
    private api: AxiosInstance;

    constructor() {
        this.api = axios.create({
            // Driven by REACT_APP_API_BASE_URL: .env points at localhost for
            // `npm start`, .env.production at the live API for `npm run build`.
            baseURL: appConfig.apiBaseUrl,
            headers: {
                'Content-Type': 'application/json',
            },
        });

        attachSession(this.api);
    }

    // User endpoints
    async getCurrentUser(): Promise<User> {
        const token = this.getAuthToken();
        if (!token) {
            throw new Error('No authentication token found');
        }

        // Decode JWT to get username (support base64url)
        const payload = readJwtClaims(token);
        if (!payload || !payload.sub) {
            throw new Error('Invalid token payload');
        }
        const response: AxiosResponse<User> = await this.api.get(`/users/username/${payload.sub}`);
        return response.data;
    }

    async updateUser(userId: number, userData: UserUpdateRequest): Promise<User> {
        const response: AxiosResponse<User> = await this.api.put(`/users/${userId}`, userData);
        return response.data;
    }

    async getAllUsers(): Promise<User[]> {
        const response: AxiosResponse<User[]> = await this.api.get('/users');
        return response.data;
    }

    async getServices(): Promise<any[]> {
        const response: AxiosResponse<[]> = await this.api.get('/services');
        return response.data;
    }

    getAuthToken(): string | null {
        return sessionStore().getItem('authToken');
    }

    isAuthenticated(): boolean {
        const token = this.getAuthToken();
        if (!token) return false;

        const payload = readJwtClaims(token);
        if (!payload || typeof payload.exp !== 'number') return false;
        return payload.exp * 1000 > Date.now();
    }

}

export const apiService = new ApiService();
export default apiService;
